import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Upload, FileText, CheckCircle2, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { clientPortalHomePath } from '../../utils/clientPortalPaths';
import {
  validateRegistrationNumber,
  registrationFieldMeta,
  SIGNATORY_NAME_META,
  SIGNATORY_ID_META,
  BANK_ACCOUNT_META,
  BANK_ACCOUNT_HOLDER_META,
  BANK_NAME_META,
  BANK_BRANCH_CODE_META,
  VAT_NUMBER_META,
} from '../../utils/cpOnboardingFormValidation';
import logoIcon from '../../assets/logo-icon.png';
import { MaskedIdField } from './MaskedIdField';

const API_BASE = '/api/v1/client-portal';

type WizardStep = 'form' | 'documents' | 'finalize';

interface RequiredDoc {
  documentType: string;
  description: string;
  required: boolean;
}

interface KybDoc {
  id: number;
  document_type: string;
  status: string;
  verification_score: number | null;
  validation_result?: { errors?: string[] } | null;
}

interface OnboardingStatus {
  onboardingPhase: string;
  companyName: string;
  clientCode: string;
  kybReference: string | null;
  formPrefill: Record<string, string>;
  formSubmitted: boolean;
  uatKybRelaxedValidation?: boolean;
  requiredDocuments: RequiredDoc[];
  documentEvaluation?: {
    missingDocuments: Array<{ documentType?: string } | string>;
    failedDocuments?: Array<{ documentType?: string; errors?: string[] } | string>;
  } | null;
}

const ENTITY_TYPES = [
  { value: 'company', label: 'Company' },
  { value: 'close_corporation', label: 'Close Corporation' },
  { value: 'sole_proprietor', label: 'Sole Proprietor' },
  { value: 'trust', label: 'Trust' },
];

const DOC_LABELS: Record<string, string> = {
  company_logo: 'Company Logo',
  cor15: 'Company registration certificate',
  cor14_3: 'CoR14.3 (Directors)',
  trust_deed: 'Trust Deed',
  id_document: 'ID Document',
  proof_of_address: 'Proof of Address',
  bank_confirmation: 'Bank Confirmation',
  tax_clearance: 'Tax clearance (SARS TCS or VAT103)',
  resolution: 'Resolution / Letter of Authority',
};

function docLabel(type: string): string {
  return DOC_LABELS[type] || type.replace(/_/g, ' ');
}

const DOC_OK_STATUSES = new Set(['validated', 'verified', 'approved']);
const DOC_PROCESSING_STATUSES = new Set(['processing', 'pending', 'extracted']);

function docUploadStatus(docs: KybDoc[], documentType: string): { uploaded?: KybDoc; ok: boolean } {
  const matches = docs.filter((d) => d.document_type === documentType);
  if (!matches.length) return { ok: false };
  const best = matches.find((d) => DOC_OK_STATUSES.has(d.status)) || matches[0];
  return { uploaded: best, ok: DOC_OK_STATUSES.has(best.status) };
}

// True if ANY uploaded document is still being analysed by the OCR worker
// (Cloud Tasks). While this is true we show a "please wait" banner so the CP
// knows a doc is in flight. We do NOT block all uploads — the CP can upload
// OTHER doc types while one is processing (the Cloud Tasks queue handles
// concurrent OCR). We only block re-uploading the SAME doc type that's
// currently processing (prevents duplicate rows).
function anyDocProcessing(docs: KybDoc[]): boolean {
  return docs.some((d) => DOC_PROCESSING_STATUSES.has(d.status));
}

function isDocTypeProcessing(docs: KybDoc[], documentType: string): boolean {
  return docs.some((d) => d.document_type === documentType && DOC_PROCESSING_STATUSES.has(d.status));
}

const LOGO_UPLOAD_HELP = [
  'Formats: PNG (preferred — transparency), JPEG, or WebP. Max 1 MB.',
  'Square (e.g. 512×512px) or horizontal wordmark (up to 5:1 wide, e.g. 640×256px).',
  'Shortest side at least 256px; longest side at most 2048px.',
  'Not accepted: SVG, GIF, PDF. Your logo appears in the portal header after upload.',
];

function validExtsForDocType(documentType: string, uatRelaxed = false): string[] {
  if (documentType === 'company_logo') {
    return uatRelaxed
      ? ['.png', '.apng', '.jpg', '.jpeg', '.webp', '.gif', '.pdf', '.txt']
      : ['.png', '.apng', '.jpg', '.jpeg', '.webp'];
  }
  if (uatRelaxed) {
    return ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.gif', '.txt', '.doc', '.docx', '.csv'];
  }
  return ['.pdf', '.jpg', '.jpeg', '.png'];
}

function fileAcceptForDocType(documentType: string, uatRelaxed = false): string {
  return validExtsForDocType(documentType, uatRelaxed).join(',');
}

export const ClientOnboardingOverlay: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser, getToken } = useClientAuth();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [docs, setDocs] = useState<KybDoc[]>([]);
  const [step, setStep] = useState<WizardStep>('form');
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const authHeaders = useCallback((): HeadersInit => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const loadStatus = useCallback(async () => {
    const res = await fetch(`${API_BASE}/onboarding/status`, { headers: authHeaders() });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to load onboarding');
    setStatus(data.data);
    setForm((prev) => ({ ...data.data.formPrefill, ...prev }));
    if (data.data.formSubmitted) setStep('documents');
    return data.data as OnboardingStatus;
  }, [authHeaders]);

  const loadDocs = useCallback(async () => {
    const res = await fetch(`${API_BASE}/kyb-documents`, { headers: authHeaders() });
    const data = await res.json();
    if (data.success) setDocs(data.data.documents || []);
  }, [authHeaders]);

  // Derived values — declared early so useEffects below can reference them
  // in their dependency arrays without hitting a temporal-dead-zone (TDZ)
  // ReferenceError. Previously `requiredDocs` was declared ~180 lines below
  // the useEffect that resets `uploadDocType`, which crashed the CP portal
  // on every render with "Cannot access 'requiredDocs' before initialization".
  const requiredDocs = status?.requiredDocuments || [];
  const regMeta = registrationFieldMeta(form.entity_type || 'company');
  const uatKybRelaxed = !!status?.uatKybRelaxedValidation;
  const docProcessing = anyDocProcessing(docs);
  const selectedDocProcessing = uploadDocType ? isDocTypeProcessing(docs, uploadDocType) : false;

  useEffect(() => {
    if (user?.onboardingPhase === 'operational') {
      navigate('/client/dashboard', { replace: true });
      return;
    }
    (async () => {
      try {
        await loadStatus();
        await loadDocs();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load onboarding');
      }
    })();
  }, [user?.onboardingPhase, navigate, loadStatus, loadDocs]);

  // Poll docs + onboarding status every 5s while on the documents OR finalize
  // step. Previously only `loadDocs` was polled on the documents step — the
  // finalize step showed a stale `documentEvaluation` from the initial
  // `loadStatus()` call, so a doc that validated 4 seconds after the finalize
  // step loaded would surface a persistent "analysis still in progress"
  // warning even though the backend had already marked it validated.
  // Now `loadStatus` is also polled on the finalize step, keeping the
  // evaluation fresh until the user clicks submit. (The wizard step id is
  // 'finalize', not 'submit' — the prior `step === 'submit'` check never
  // matched, so polling silently no-op'd on the finalize step.)
  useEffect(() => {
    if (step === 'documents' || step === 'finalize') {
      pollRef.current = setInterval(() => {
        loadDocs();
        if (step === 'finalize') loadStatus();
      }, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [step, loadDocs, loadStatus]);

  useEffect(() => {
    setUploadFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }, [uploadDocType]);

  // If the currently-selected upload doc type just became validated (required
  // docs are hidden from the dropdown once validated), reset the selection so
  // the CP cannot upload to a hidden doc type and sees the placeholder instead.
  useEffect(() => {
    if (!uploadDocType) return;
    const rd = requiredDocs.find((r) => r.documentType === uploadDocType);
    if (!rd) return;
    if (rd.required && docUploadStatus(docs, uploadDocType).ok) {
      setUploadDocType('');
    }
  }, [uploadDocType, docs, requiredDocs]);

  const handleFileSelect = useCallback((f: File) => {
    if (!uploadDocType) {
      setError('Select a document type first.');
      return;
    }
    const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
    const allowed = validExtsForDocType(uploadDocType, !!status?.uatKybRelaxedValidation);
    if (!allowed.includes(ext)) {
      setError(`Unsupported file type. Accepted: ${allowed.join(', ')}`);
      return;
    }
    setUploadFile(f);
    setError('');
  }, [uploadDocType, status?.uatKybRelaxedValidation]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  }, [handleFileSelect]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (uploadDocType) setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleLogout = () => {
    logout();
    navigate('/client/login', { replace: true });
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setMsg('');

    const required: Array<[string, string]> = [
      ['legal_name', 'Legal name'],
      ['reg_number', 'Registration number'],
      ['registered_address', 'Registered address'],
      ['signatory_name', 'Authorised signatory name'],
      ['signatory_id', 'Signatory ID or passport'],
    ];
    const missing = required.filter(([key]) => !String(form[key] || '').trim());
    if (missing.length) {
      setError(`Please complete: ${missing.map(([, label]) => label).join(', ')}.`);
      setBusy(false);
      return;
    }

    const regCheck = validateRegistrationNumber(form.reg_number || '', form.entity_type || 'company');
    if (!regCheck.valid) {
      setError(regCheck.error);
      setBusy(false);
      return;
    }

    const payload = { ...form };
    if (!payload.account_number?.trim()) delete payload.account_number;

    try {
      const res = await fetch(`${API_BASE}/onboarding/submit`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ form: payload }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Save failed (${res.status})`);
      }
      await refreshUser();
      await loadStatus();
      setStep('documents');
      setMsg('Form saved. Please upload your KYB documents.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  const uploadDocument = async () => {
    if (!uploadDocType || !uploadFile) return;
    setBusy(true);
    setError('');
    const fd = new FormData();
    fd.append('document', uploadFile);
    fd.append('document_type', uploadDocType);
    try {
      const res = await fetch(`${API_BASE}/kyb-documents`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Upload failed');
      setUploadFile(null);
      setUploadDocType('');
      if (fileRef.current) fileRef.current.value = '';
      await loadDocs();
      await loadStatus();
      await refreshUser();
      setMsg('Document uploaded.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/onboarding/finalize`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not submit for review');
      await refreshUser();
      await loadStatus();
      setMsg('Submitted to MyMoolah for review. We will email you when your account is activated.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Finalize failed');
    } finally {
      setBusy(false);
    }
  };

  const phase = status?.onboardingPhase || user?.onboardingPhase || 'onboarding';
  const companyName = status?.companyName || user?.companyName || 'Partner';

  if (phase === 'pending_admin_review') {
    return (
      <Shell companyName={companyName} onLogout={handleLogout}>
        <StatusCard
          icon={<Clock className="h-10 w-10 text-sky-600" />}
          title="Under review by MyMoolah"
          body={`Thank you. Your onboarding pack${status?.kybReference ? ` (${status.kybReference})` : ''} is with our compliance team. You will receive an email when your account is activated and disbursements are enabled.`}
        />
      </Shell>
    );
  }

  if (phase === 'rejected') {
    return (
      <Shell companyName={companyName} onLogout={handleLogout}>
        <StatusCard
          icon={<AlertCircle className="h-10 w-10 text-red-600" />}
          title="Onboarding not approved"
          body="Your application was not approved. Please contact support@mymoolah.africa for assistance."
        />
      </Shell>
    );
  }

  return (
    <Shell companyName={companyName} onLogout={handleLogout}>
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--foreground)]">Company details and documents</h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Your agreements come first. After they are accepted, complete these three steps in order: company details, documents, then submit for review.
          </p>
        </div>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        ) : null}
        {msg ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{msg}</div>
        ) : null}

        <div className="flex gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
          {(['form', 'documents', 'finalize'] as WizardStep[]).map((s, i) => (
            <span
              key={s}
              className={`rounded-full px-3 py-1 ${step === s ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'bg-[var(--muted)]'}`}
            >
              {i + 1}. {s === 'form' ? 'Company details' : s === 'documents' ? 'Documents' : 'Submit'}
            </span>
          ))}
        </div>

        {step === 'form' && (
          <form onSubmit={submitForm} className="rounded-xl border border-[var(--border)] bg-white p-6 space-y-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <FileText className="h-5 w-5" /> Company details
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Legal name *" value={form.legal_name || ''} onChange={(v) => setForm((p) => ({ ...p, legal_name: v }))} required />
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Entity type *</label>
                <select
                  value={form.entity_type || 'company'}
                  onChange={(e) => setForm((p) => ({ ...p, entity_type: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  {ENTITY_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <Field
                label={regMeta.label}
                value={form.reg_number || ''}
                onChange={(v) => setForm((p) => ({ ...p, reg_number: v }))}
                required
                helperText={regMeta.helperText}
                placeholder={regMeta.placeholder}
              />
              <Field label="Expected monthly volume (ZAR)" value={form.expected_monthly_volume || ''} onChange={(v) => setForm((p) => ({ ...p, expected_monthly_volume: v }))} type="number" />
              <Field
                label={SIGNATORY_NAME_META.label}
                value={form.signatory_name || ''}
                onChange={(v) => setForm((p) => ({ ...p, signatory_name: v }))}
                required
                helperText={SIGNATORY_NAME_META.helperText}
                placeholder={SIGNATORY_NAME_META.placeholder}
              />
              <MaskedIdField
                id="signatory_id"
                label={SIGNATORY_ID_META.label}
                value={form.signatory_id || ''}
                onChange={(v) => setForm((p) => ({ ...p, signatory_id: v }))}
                required
                helperText={SIGNATORY_ID_META.helperText}
                placeholder={SIGNATORY_ID_META.placeholder}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Registered address *</label>
              <textarea
                required
                rows={2}
                value={form.registered_address || ''}
                onChange={(e) => setForm((p) => ({ ...p, registered_address: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </div>
            <MaskedIdField
              id="account_number"
              label={BANK_ACCOUNT_META.label}
              value={form.account_number || ''}
              onChange={(v) => setForm((p) => ({ ...p, account_number: v }))}
              helperText={BANK_ACCOUNT_META.helperText}
              placeholder={BANK_ACCOUNT_META.placeholder}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={BANK_ACCOUNT_HOLDER_META.label}
                value={form.bank_account_holder || ''}
                onChange={(v) => setForm((p) => ({ ...p, bank_account_holder: v }))}
                helperText={BANK_ACCOUNT_HOLDER_META.helperText}
                placeholder={BANK_ACCOUNT_HOLDER_META.placeholder}
              />
              <Field
                label={BANK_NAME_META.label}
                value={form.bank_name || ''}
                onChange={(v) => setForm((p) => ({ ...p, bank_name: v }))}
                helperText={BANK_NAME_META.helperText}
                placeholder={BANK_NAME_META.placeholder}
              />
              <Field
                label={BANK_BRANCH_CODE_META.label}
                value={form.bank_branch_code || ''}
                onChange={(v) => setForm((p) => ({ ...p, bank_branch_code: v.replace(/\D/g, '').slice(0, 10) }))}
                helperText={BANK_BRANCH_CODE_META.helperText}
                placeholder={BANK_BRANCH_CODE_META.placeholder}
              />
              <div>
                <label className="mb-1 flex items-center gap-2 text-xs font-medium text-gray-600">
                  <input
                    type="checkbox"
                    checked={form.is_vat_vendor === 'true'}
                    onChange={(e) => setForm((p) => ({ ...p, is_vat_vendor: e.target.checked ? 'true' : '' }))}
                    className="rounded border-gray-300"
                  />
                  Registered VAT vendor
                </label>
                {form.is_vat_vendor === 'true' && (
                  <input
                    type="text"
                    value={form.vat_number || ''}
                    onChange={(e) => setForm((p) => ({ ...p, vat_number: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    placeholder={VAT_NUMBER_META.placeholder}
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                )}
                {form.is_vat_vendor === 'true' && (
                  <p className="mt-1 text-xs text-gray-400">{VAT_NUMBER_META.helperText}</p>
                )}
              </div>
            </div>
            <button type="submit" disabled={busy} className="rounded-lg bg-[var(--primary)] px-5 py-2.5 text-sm font-semibold text-[var(--primary-foreground)] disabled:opacity-50">
              {busy ? 'Saving…' : 'Save and continue to documents'}
            </button>
          </form>
        )}

        {step === 'documents' && (
          <div className="rounded-xl border border-[var(--border)] bg-white p-6 space-y-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Upload className="h-5 w-5" /> KYB documents
            </h2>
            <p className="text-xs text-gray-500">
              Upload clear JPG, PNG, or PDF for KYB documents. Company logo: PNG/JPEG/WebP only (see requirements below).
            </p>
            {uatKybRelaxed ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <strong>UAT mode:</strong> any placeholder file is accepted for each document type. Production runs full OpenAI KYB validation.
              </div>
            ) : null}

            {uploadDocType === 'company_logo' && (
              <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 text-xs text-sky-900">
                <p className="font-semibold">Company logo requirements</p>
                <ul className="mt-2 list-disc space-y-1 pl-4">
                  {LOGO_UPLOAD_HELP.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}

            {requiredDocs.length > 0 && (
              <ul className="space-y-2 text-sm">
                {requiredDocs.map((rd) => {
                  const { uploaded, ok } = docUploadStatus(docs, rd.documentType);
                  const failed = uploaded && uploaded.status === 'validation_failed';
                  const processing = uploaded && (uploaded.status === 'processing' || uploaded.status === 'pending' || uploaded.status === 'extracted');
                  const firstError = uploaded?.validation_result?.errors?.[0];
                  return (
                    <li key={rd.documentType} className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${ok ? 'bg-emerald-500' : failed ? 'bg-red-500' : uploaded ? 'bg-amber-400' : 'bg-gray-300'}`} />
                        <span>{rd.description || docLabel(rd.documentType)}</span>
                        {processing && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                            — analysing your document…
                          </span>
                        )}
                      </div>
                      {failed && firstError && (
                        <div className="ml-4 rounded border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-800">
                          <p className="font-semibold">We couldn&apos;t verify this document: {firstError}</p>
                          <p className="mt-1 text-red-700">Please re-upload a clearer, well-lit scan of the whole page (no shadows, no cut-off edges).</p>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {docProcessing && (
              <div className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
                <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-[var(--primary)]" aria-hidden />
                <div>
                  <p className="font-semibold">A document is being analysed…</p>
                  <p className="text-xs text-sky-800">
                    This typically takes 10–30 seconds. You can upload another document type now, or wait for this one to finish.
                    This page refreshes automatically.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3 rounded-lg bg-gray-50 p-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-500">Document type</label>
                <select
                  value={uploadDocType}
                  onChange={(e) => setUploadDocType(e.target.value)}
                  disabled={false}
                  className="w-full max-w-md rounded-lg border px-3 py-2 text-sm sm:w-auto"
                >
                  <option value="">Select…</option>
                  {requiredDocs
                    .filter((rd) => {
                      // One slot per document type. Hide types that already have
                      // a current upload (including optional tax clearance) so
                      // VAT103 and SARS TCS cannot create two Tax Clearance rows.
                      // Rejected / failed uploads stay available for replacement.
                      // Admin reject re-enables the option.
                      const matches = docs.filter((d) => d.document_type === rd.documentType);
                      return !matches.some((d) => !['rejected', 'validation_failed', 'expired'].includes(d.status));
                    })
                    .map((rd) => (
                      <option key={rd.documentType} value={rd.documentType}>
                        {rd.description || docLabel(rd.documentType)}
                      </option>
                    ))}
                </select>
              </div>

              <div
                onDrop={selectedDocProcessing ? undefined : handleDrop}
                onDragOver={selectedDocProcessing ? undefined : handleDragOver}
                onDragLeave={selectedDocProcessing ? undefined : handleDragLeave}
                onClick={() => !selectedDocProcessing && uploadDocType && fileRef.current?.click()}
                className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-colors ${
                  selectedDocProcessing
                    ? 'cursor-not-allowed border-gray-200 bg-gray-100/50 opacity-60'
                    : !uploadDocType
                    ? 'cursor-not-allowed border-gray-200 bg-gray-100/50 opacity-70'
                    : isDragging
                    ? 'cursor-pointer border-[var(--primary)] bg-[var(--primary)]/5'
                    : uploadFile
                    ? 'cursor-pointer border-emerald-300 bg-emerald-50'
                    : 'cursor-pointer border-[var(--border)] hover:border-[var(--primary)]/50 hover:bg-white'
                }`}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept={uploadDocType ? fileAcceptForDocType(uploadDocType, uatKybRelaxed) : (uatKybRelaxed ? '.pdf,.jpg,.jpeg,.png,.webp,.gif,.txt,.doc,.docx,.csv' : '.pdf,.jpg,.jpeg,.png,.webp')}
                  className="hidden"
                  disabled={selectedDocProcessing || !uploadDocType}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelect(f);
                  }}
                />

                {uploadFile ? (
                  <>
                    <FileText className="mb-3 h-10 w-10 text-emerald-600" aria-hidden />
                    <p className="text-sm font-medium text-[var(--foreground)]">{uploadFile.name}</p>
                    <p className="mt-1 text-xs text-[var(--muted-foreground)]">Click or drop to replace</p>
                  </>
                ) : (
                  <>
                    <Upload className="mb-3 h-10 w-10 text-[var(--muted-foreground)]/50" aria-hidden />
                    <p className="text-sm font-medium text-[var(--foreground)]">
                      {!uploadDocType
                        ? 'Select a document type above first'
                        : uploadDocType === 'company_logo'
                        ? 'Drop your company logo here'
                        : 'Drop your document here'}
                    </p>
                    <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                      {!uploadDocType
                        ? 'Then drag & drop or click to browse'
                        : uploadDocType === 'company_logo'
                        ? 'PNG, JPEG, or WebP — max 1 MB'
                        : 'PDF, JPG, or PNG'}
                    </p>
                  </>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={uploadDocument}
                  disabled={busy || selectedDocProcessing || !uploadDocType || !uploadFile}
                  className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {busy ? 'Uploading…' : selectedDocProcessing ? 'Analysing previous upload…' : 'Upload'}
                </button>
              </div>
            </div>

            <div className="flex justify-between">
              <button type="button" onClick={() => setStep('form')} className="text-sm text-gray-600 hover:underline">Back to form</button>
              <button type="button" onClick={() => setStep('finalize')} className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-white">
                Continue to submit
              </button>
            </div>
          </div>
        )}

        {step === 'finalize' && (
          <div className="rounded-xl border border-[var(--border)] bg-white p-6 space-y-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <CheckCircle2 className="h-5 w-5" /> Submit for MyMoolah review
            </h2>
            <p className="text-sm text-gray-600">
              When you submit, our team will verify your documents and run compliance checks. Disbursements unlock after approval.
            </p>

            {(() => {
              const failed = status?.documentEvaluation?.failedDocuments || [];
              const missing = status?.documentEvaluation?.missingDocuments || [];
              const pendingDocs = missing.filter((m) => typeof m === 'object' && m.note && /not yet validated/i.test(m.note));
              if (failed.length === 0 && pendingDocs.length === 0) return null;
              return (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-semibold">Please fix these before submitting:</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {failed.map((f, i) => (
                      <li key={`f-${i}`}>
                        {typeof f === 'object' ? docLabel(f.documentType || '') : docLabel(String(f))}
                        {typeof f === 'object' && f.errors?.[0] ? ` — ${f.errors[0]}` : ''}
                      </li>
                    ))}
                    {pendingDocs.map((p, i) => (
                      <li key={`p-${i}`}>
                        {typeof p === 'object' ? docLabel(p.documentType || '') : docLabel(String(p))} — analysis still in progress, please wait a moment.
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-amber-800">Re-upload a clearer scan of any failed document, or wait for analysis to complete, then come back here.</p>
                </div>
              );
            })()}

            <button type="button" onClick={finalize} disabled={busy || ((status?.documentEvaluation?.failedDocuments || []).length > 0)}
              className="rounded-lg bg-[var(--primary)] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
              {busy ? 'Submitting…' : 'Submit onboarding for review'}
            </button>
            <button type="button" onClick={() => setStep('documents')} className="block text-sm text-gray-600 hover:underline">
              Back to documents
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
};

function Shell({ companyName, onLogout, children }: { companyName: string; onLogout: () => void; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--background)]">
      <header className="flex h-14 items-center justify-between border-b border-[var(--border)] bg-white px-4 sm:px-6">
        <div className="flex items-center gap-2">
          <img src={logoIcon} alt="" className="h-8 w-8 rounded-lg" />
          <div>
            <p className="text-sm font-semibold">{companyName}</p>
            <p className="text-xs text-[var(--muted-foreground)]">Partner onboarding</p>
          </div>
        </div>
        <button type="button" onClick={onLogout} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>
      <main className="px-4 py-8 sm:px-6">{children}</main>
    </div>
  );
}

function StatusCard({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="mx-auto max-w-lg rounded-xl border border-[var(--border)] bg-white p-8 text-center space-y-4">
      <div className="flex justify-center">{icon}</div>
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Field({
  label, value, onChange, required, type = 'text', helperText, placeholder, autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
  helperText?: string;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
      />
      {helperText ? (
        <p className="mt-1 text-xs text-[var(--muted-foreground)] leading-snug">{helperText}</p>
      ) : null}
    </div>
  );
}

export default ClientOnboardingOverlay;
